<template>
    <v-dialog v-model="modal" :scrim="false" width="600">
        <v-card v-if="infoLoading" color="primary" dark>
            <v-card-text>
                {{ $t('loading') }}
                <v-progress-linear indeterminate color="white" class="mb-0" />
            </v-card-text>
        </v-card>
        <v-card v-else>
            <v-icon class="v-card-close" @click="modal = false">mdi-close</v-icon>
            <v-card-title class="text-wrap">
                {{ roomInfo.name }}
            </v-card-title>
            <v-card-text>
                <div
                    v-if="
                        !roomInfo.images.image.length &&
                        roomInfo.description === roomInfo.name &&
                        !roomInfo.services.length &&
                        !tariffDetails.loading &&
                        !tariffDetails.tariff &&
                        !tariffDetails.tariffDescription
                    "
                >
                    {{ $t('no_details') }}
                </div>
                <v-carousel
                    v-if="roomInfo.images.image.length"
                    height="300"
                    :show-arrows="roomInfo.images.image.length > 1"
                    :hide-delimiters="roomInfo.images.image.length === 1"
                    hide-delimiter-background
                    class="mb-5"
                >
                    <v-carousel-item
                        v-for="(image, imageIndex) in roomInfo.images.image"
                        :key="imageIndex"
                        :src="$filters.imageUrl(image.url, 600)"
                        reverse-transition="fade-transition"
                        transition="fade-transition"
                        cover
                        @click="showPhotoSwipe(imageIndex)"
                    />
                </v-carousel>
                <template v-if="roomInfo.bedTypes && roomInfo.bedTypes.length">
                    <hotel-bed-type-label :bed-types="roomInfo.bedTypes" />
                </template>
                <template v-if="showShortDescription(roomInfo)">
                    <strong>{{ $t('description_type.short') }}</strong>
                    <br />
                    <div v-html="roomInfo.shortDescription" />
                </template>
                <template v-if="roomInfo.description && roomInfo.description !== roomInfo.name">
                    <strong>{{ $t('description_type.general') }}</strong>
                    <br />
                    <div v-html="roomInfo.description" />
                </template>
                <hotel-room-services :room-info="roomInfo" />
                <template v-if="offerKey">
                    <v-skeleton-loader
                        v-if="tariffDetails.loading"
                        transition="fade-transition"
                        height="100"
                        type="list-item-two-line"
                    />
                    <template
                        v-if="!tariffDetails.loading && (tariffDetails.tariff || tariffDetails.tariffDescription)"
                    >
                        <strong>{{ $t('tariff_description') }}</strong>
                        <div v-if="tariffDetails.tariff" v-html="tariffDetails.tariff" />
                        <div v-if="tariffDetails.tariffDescription" v-html="tariffDetails.tariffDescription" />
                    </template>
                </template>
            </v-card-text>
        </v-card>
        <v-photo-swipe
            v-if="photoSwipeItems?.length"
            ref="photoSwipe"
            :items="photoSwipeItems"
            :photo-swipe-options="photoSwipeOptions"
        />
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import {isEqual} from '@/utils/helpers'
    import {EventBus, SHOW_ROOM_INFO_MODAL_EVENT} from '@/utils/event-bus'
    import VPhotoSwipe from '@/components/snippets/VPhotoSwipe.vue'
    import HotelRoomServices from '~src/components/hotels/snippets/hotelRoomServices.src'
    import HotelBedTypeLabel from '@/components/hotels/snippets/HotelBedTypeLabel.vue'

    @Component({components: {VPhotoSwipe, HotelRoomServices, HotelBedTypeLabel}})
    export class HotelRoomInfoModal extends Vue {
        infoLoading = false
        modal = false
        tariffDetails = {
            loading: true,
        }
        roomInfo = {
            images: {
                image: [],
            },
            services: [],
        }
        offerKey = null
        photoSwipeOptions = {
            index: 0,
        }

        mounted() {
            EventBus.$on(SHOW_ROOM_INFO_MODAL_EVENT, this.showModal)
        }

        beforeUnmount() {
            EventBus.$off(SHOW_ROOM_INFO_MODAL_EVENT, this.showModal)
        }

        async showModal({hotel, room, roomInfo = null}) {
            //roomInfo is for orderDetails. we don't need to load hotelInfo

            this.modal = true
            this.infoLoading = true
            let roomInfoPrepared

            if (roomInfo) {
                roomInfoPrepared = roomInfo
            } else {
                //TODO After fix API find by code
                let hotelsInfo = hotelsRuntimeStore.getHotelsInfoByRoomOffer(room, hotel) || null

                if (!hotelsInfo) {
                    await hotelsRuntimeStore.loadHotelsInfoByOffer(hotel, [room.hotelSupplierCode])
                    hotelsInfo = hotelsRuntimeStore.getHotelsInfoByRoomOffer(room, hotel) || null
                }

                roomInfoPrepared = hotelsInfo?.hotelDescriptionInfo?.roomTypes.roomType.find(
                    roomType => roomType.code === room.code || roomType.code === room.roomCode
                )
            }

            if (roomInfoPrepared) {
                this.roomInfo = roomInfoPrepared
            } else {
                this.roomInfo = {
                    images: {
                        image: [],
                    },
                    services: [],
                    ...(room?.roomTypeDescription && {description: room.roomTypeDescription}),
                }
            }

            this.offerKey = room?.groupedOffers?.[0]?.offerKey
            if (this.offerKey) {
                this.loadTariffDetails()
            }

            this.infoLoading = false
        }

        async loadTariffDetails() {
            if (!hotelsStore.tariffDetails[this.offerKey]) {
                await hotelsStore.getTariffDetails({offerKey: this.offerKey})
            }

            this.tariffDetails = hotelsStore.tariffDetails[this.offerKey]
        }

        showShortDescription(info) {
            return (
                info.shortDescription &&
                info.shortDescription !== info.name &&
                (!info.description || info.description.indexOf(info.shortDescription) === -1)
            )
        }

        showPhotoSwipe(index) {
            this.photoSwipeOptions.index = index
            this.$refs.photoSwipe.openLightbox(index)
        }

        get images() {
            return this.roomInfo && this.roomInfo.images ? this.roomInfo.images.image : []
        }

        get photoSwipeItems() {
            return this.images.map(({url, title}) => ({
                src: this.$filters.imageUrl(url, 800),
                w: 0,
                h: 0,
                title,
            }))
        }
    }

    export default HotelRoomInfoModal
</script>

<style scoped>
    :deep(.description img) {
        max-width: 300px;
        height: auto;
    }
</style>
